import React, { useCallback, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  beautyCancelAppointmentApi,
  beautyCancelSeriesApi,
  beautyCheckinAppointmentApi,
  beautyConfirmAppointmentApi,
  beautyDeleteBlockApi,
  beautyNoShowAppointmentApi,
  beautyRescheduleAppointmentApi,
  beautyRescheduleOptionsApi,
} from "../../../api/organizerBeautyApi";
import AgendaToolbar, { AgendaViewTabs } from "../../components/AgendaToolbar";
import RescheduleModal from "../../../pages/reservas/components/RescheduleModal";
import SpinLoading from "../../../components/spinLoading/SpinLoading";
import { ConfirmModal, OrgToast } from "../TurnosUi";
import BeautyAgendaGrid from "./BeautyAgendaGrid";
import BeautyAgendaTabla from "./BeautyAgendaTabla";
import BloqueoModal from "./BloqueoModal";
import NuevaReservaModal from "./NuevaReservaModal";
import TurnoDetalle from "./TurnoDetalle";
import TurnosFijosModal from "./TurnosFijosModal";
import { CancelarSerieModal, FechasMantenidasModal } from "./CancelarSerieModal";
import { cancelacionMensaje, seriesErrorMessage, tieneSerie } from "./turnoFijoModel";
import { seriesCreatedMessage } from "../../reservas/components/courtSeriesModel";
import useBeautyAgenda from "./useBeautyAgenda";
import ListaEsperaDelDia from "../../listaEspera/ListaEsperaDelDia";
import { isWaitlistEnabled } from "../../../pages/listaDeEspera/waitlistModel";
import { buildAgendaModel, hayPreparacion, haySerie, hoyISO, mensajeDe, nombreCliente, plata, toHHmm } from "./agendaLayout";
import "../../reservas/reservas-organizer.css";
import "../../reservas/components/OrganizerDailyCourtGrid.css";
import "./agenda.css";

/* ─────────────────────────────────────────
   AgendaTab — agenda del día por profesional (GET /agenda): turnos, bloqueos, nueva reserva y
   confirmar / cancelar / no asistió. "Solo mis turnos" lo resuelve el back: acá puede llegar una sola columna.
───────────────────────────────────────── */

const ACCIONES = {
  confirm: { api: beautyConfirmAppointmentApi, titulo: "Confirmar turno", boton: "Confirmar", ok: "Turno confirmado", error: "No pudimos confirmar el turno. Probá de nuevo." },
  cancel: { api: beautyCancelAppointmentApi, titulo: "Cancelar turno", boton: "Cancelar turno", ok: "Turno cancelado", error: "No pudimos cancelar el turno. Probá de nuevo." },
  checkin: { api: beautyCheckinAppointmentApi, titulo: "Marcar ingreso", boton: "Marcar ingreso", ok: "Ingreso marcado correctamente", error: "No pudimos marcar el ingreso. Probá de nuevo.", yaPresente: "El turno ya fue marcado como presente." },
  "no-show": { api: beautyNoShowAppointmentApi, titulo: "Marcar como no asistió", boton: "Marcar", ok: "Turno marcado como no asistió", error: "No pudimos actualizar el turno. Probá de nuevo." },
};

const textoAccion = (tipo, turno) => {
  const quien = `el turno de ${nombreCliente(turno)}`;
  if (tipo === "confirm") return `Vas a confirmar ${quien}.`;
  if (tipo === "checkin") return `Vas a marcar el ingreso en ${quien}. Esta acción no se puede deshacer.`;
  if (tipo === "no-show") return `Vas a marcar ${quien} como "No asistió".`;
  const sena = Number(turno.depositAmount) || 0;
  return sena > 0
    ? `Vas a cancelar ${quien}. Tiene una seña de ${plata(sena)}: el reintegro es manual, tenés que devolverla vos.`
    : `Vas a cancelar ${quien}.`;
};

const Aviso = ({ children }) => (
  <div className="zr-panel" style={{ padding: 0 }}>
    <div className="zr-empty" style={{ flexDirection: "column", gap: 12, textAlign: "center", padding: 24 }}>
      {children}
    </div>
  </div>
);

const AgendaTab = ({ salon, onIrAProfesionales }) => {
  const esEmpleado = useSelector((s) => s.auth)?.user?.tipo === "empleado";
  const [date, setDate] = useState(hoyISO);
  const [mostrarCancelados, setMostrarCancelados] = useState(false);
  const [vista, setVista] = useState("grid"); // "grid" | "table"
  const { agenda, state, message, reload, reloadAndWait } = useBeautyAgenda({ date, includeCancelled: mostrarCancelados });
  const [detalle, setDetalle] = useState(null);
  const [accion, setAccion] = useState(null); // { tipo, turno }
  const [ocupado, setOcupado] = useState(false);
  const [reserva, setReserva] = useState(null); // inicial del modal
  const [bloqueo, setBloqueo] = useState(null);
  const [aBorrar, setABorrar] = useState(null);
  const [mover, setMover] = useState(null); // turno que se está moviendo
  const [verSeries, setVerSeries] = useState(false); // lista de turnos fijos
  const [mantenidas, setMantenidas] = useState(null); // fechas que "Este y los siguientes" dejó en pie
  const [toast, setToast] = useState("");
  const cerrarToast = useCallback(() => setToast(""), []);

  const profesionales = useMemo(() => agenda?.professionals || [], [agenda]);
  const model = useMemo(() => buildAgendaModel(profesionales, salon?.slotStepMinutes), [profesionales, salon]);
  const columnas = model.columns;
  const puedeCrear = date >= hoyISO();

  const metrics = useMemo(() => {
    let total = 0;
    let confirmed = 0;
    let pending = 0;
    let noShow = 0;
    let cancelled = 0;
    (agenda?.professionals || []).forEach((p) => {
      (p.appointments || []).forEach((a) => {
        total += 1;
        if (a.status === "confirmed") confirmed += 1;
        else if (a.status === "pending" || a.status === "reserved") pending += 1;
        else if (a.status === "no_show") noShow += 1;
        else if (a.status === "cancelled") cancelled += 1;
      });
    });
    return { total, confirmed, pending, noShow, cancelled };
  }, [agenda]);

  const refrescar = () => reloadAndWait().catch(() => {});

  const ejecutarAccion = async () => {
    const { tipo, turno } = accion;
    const cfg = ACCIONES[tipo];
    setOcupado(true);
    try {
      const respuesta = await cfg.api(turno._id);
      setAccion(null);
      // The manual check-in endpoint answers 200 with isSuccess=false when it did not mark anything (e.g. already present).
      if (cfg.yaPresente && respuesta?.data?.isSuccess !== true) {
        setToast(respuesta?.data?.message || cfg.yaPresente);
        await refrescar();
        return;
      }
      setToast(cfg.ok);
      await refrescar();
    } catch (e) {
      setAccion(null);
      if (tipo === "checkin" && e?.response?.status === 403) {
        // Outside the scan window: same message as Canchas; reload because the state may have changed.
        setToast(mensajeDe(e, "No se puede marcar el ingreso fuera de la ventana permitida."));
        await refrescar();
      } else {
        setToast(mensajeDe(e, cfg.error));
      }
    } finally {
      setOcupado(false);
    }
  };

  // "Este y los siguientes": the occurrence being cancelled is from the day the agenda shows.
  const cancelarSiguientes = async () => {
    const { turno } = accion;
    setOcupado(true);
    try {
      const respuesta = await beautyCancelSeriesApi(turno.series.id, date);
      const resultado = respuesta?.data?.info || {};
      setAccion(null);
      if (Array.isArray(resultado.kept) && resultado.kept.length > 0) setMantenidas(resultado);
      setToast(cancelacionMensaje(resultado));
      await refrescar();
    } catch (e) {
      setAccion(null);
      setToast(seriesErrorMessage(e, "No pudimos cancelar el turno fijo. Probá de nuevo."));
      if (e?.response?.status === 409) await refrescar();
    } finally {
      setOcupado(false);
    }
  };

  const borrarBloqueo = async () => {
    setOcupado(true);
    try {
      await beautyDeleteBlockApi(aBorrar._id);
      setToast("Bloqueo eliminado");
      setABorrar(null);
      await refrescar();
    } catch (e) {
      setABorrar(null);
      setToast(mensajeDe(e, "No pudimos eliminar el bloqueo. Probá de nuevo."));
    } finally {
      setOcupado(false);
    }
  };

  const movido = async () => {
    setMover(null);
    setToast("Turno reagendado");
    await refrescar();
  };

  const creado = async (mensaje) => {
    setReserva(null);
    setBloqueo(null);
    setToast(mensaje);
    await refrescar();
  };

  const abrirReserva = (inicial = {}) => setReserva({ date: puedeCrear ? date : hoyISO(), ...inicial });
  const abrirBloqueo = (inicial = {}) => setBloqueo({ date: puedeCrear ? date : hoyISO(), ...inicial });

  const controles = (
    <AgendaToolbar
      date={date}
      onDateChange={setDate}
      showCancelled={mostrarCancelados}
      onShowCancelledChange={setMostrarCancelados}
      cancelledLabel="Mostrar cancelados"
    >
      <button type="button" className="zr-btn zr-btn--dark" onClick={() => abrirReserva()}>Nueva reserva</button>
      <button type="button" className="zr-btn zr-btn--soft" onClick={() => abrirBloqueo()}>Bloquear horario</button>
      <button type="button" className="zr-btn zr-btn--soft" onClick={() => setVerSeries(true)}>Turnos fijos</button>
    </AgendaToolbar>
  );

  let cuerpo;
  if (state === "error") {
    cuerpo = (
      <div className="org-page-error">
        <p>{message}</p>
        <button type="button" onClick={reload}>Reintentar</button>
      </div>
    );
  } else if (!agenda) {
    cuerpo = <SpinLoading />;
  } else if (!columnas.length) {
    cuerpo = (
      <Aviso>
        <p style={{ margin: 0 }}>Todavía no cargaste profesionales.</p>
        <p style={{ margin: 0 }}>Cargá a tu equipo y sus horarios para ver la agenda y recibir turnos.</p>
        {onIrAProfesionales && (
          <button type="button" className="zr-btn zr-btn--dark" onClick={onIrAProfesionales}>Agregá profesionales</button>
        )}
      </Aviso>
    );
  } else if (model.nadieAtiende) {
    cuerpo = (
      <Aviso>
        <p style={{ margin: 0 }}>Nadie atiende este día.</p>
        <p style={{ margin: 0 }}>Revisá el horario del salón y el de cada profesional, o probá con otro día.</p>
      </Aviso>
    );
  } else if (vista === "table") {
    cuerpo = (
      <BeautyAgendaTabla
        model={model}
        mostrarCancelados={mostrarCancelados}
        onTurnoSelect={setDetalle}
        onAccion={(tipo, turno) => setAccion({ tipo, turno })}
        onMover={setMover}
      />
    );
  } else {
    cuerpo = (
      <>
        {(hayPreparacion(model) || haySerie(model)) && (
          <div className="odcg-legend">
            {hayPreparacion(model) && (
              <span className="odcg-legend__item">
                <span className="odcg-legend__swatch" aria-hidden="true" />
                Preparación: el horario queda tomado, el cliente no lo ve
              </span>
            )}
            {haySerie(model) && (
              <span className="odcg-legend__item">
                <span className="odcg-item-series ba-serie-chip" aria-hidden="true">Fijo</span>
                Turno fijo
              </span>
            )}
          </div>
        )}
        <BeautyAgendaGrid
          date={date}
          model={model}
          mostrarCancelados={mostrarCancelados}
          turnoSeleccionadoId={detalle?._id}
          puedeCrear={puedeCrear}
          onFreeSelect={({ professionalId, date: d, startMin }) =>
            abrirReserva({ date: d, professionalId, slotStart: toHHmm(startMin) })
          }
          onTurnoSelect={setDetalle}
          onBloqueoSelect={setABorrar}
        />
      </>
    );
  }

  return (
    <div>
      <AgendaViewTabs viewMode={vista} onChange={setVista} />

      {/* KPI cards */}
      <Row className="zr-kpis g-3" style={{ marginBottom: "20px" }}>
        {[
          {
            key: "total",
            label: "Total",
            value: String(metrics.total).padStart(2, "0"),
            icon: (
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="5" y="4.5" width="14" height="16" rx="2" />
                <path d="M9 4.5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4.5" />
                <path d="M8.5 10h7M8.5 13.5h7M8.5 17h4" />
              </svg>
            ),
          },
          {
            key: "confirmed",
            label: "Confirmados",
            value: String(metrics.confirmed).padStart(2, "0"),
            icon: (
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="4" />
                <path d="m9 11 2 2 4-4" />
              </svg>
            ),
          },
          {
            key: "pending",
            label: "Pendientes",
            value: String(metrics.pending).padStart(2, "0"),
            icon: (
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3.5 2" />
              </svg>
            ),
          },
          {
            key: "noShow",
            label: "No asistió",
            value: String(metrics.noShow).padStart(2, "0"),
            icon: (
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v5M12 16h.01" />
              </svg>
            ),
          },
          {
            key: "cancelled",
            label: "Cancelados",
            value: String(metrics.cancelled).padStart(2, "0"),
            icon: (
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="8.5" />
                <path d="m6 6 12 12" />
              </svg>
            ),
          },
        ].map((k) => (
          <Col key={k.key}>
            <div className="zr-kpi">
              <span className="zr-kpi__icon">{k.icon}</span>
              <div>
                <div className="zr-kpi__label">{k.label}</div>
                <div className="zr-kpi__value">{k.value}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <div className="odcg-panel">
        {controles}
        <ListaEsperaDelDia
          enabled={isWaitlistEnabled(salon)}
          barId={salon?._id}
          kind="beauty"
          date={date}
          profesionales={columnas}
          onToast={setToast}
        />
        {cuerpo}

      {detalle && (
        <TurnoDetalle
          turno={detalle}
          puedeDesvincular={!esEmpleado}
          onClose={() => setDetalle(null)}
          onMover={(t) => {
            setMover(t);
            setDetalle(null);
          }}
          onAccion={(tipo) => {
            setAccion({ tipo, turno: detalle });
            setDetalle(null);
          }}
          onVerSeries={() => {
            setDetalle(null);
            setVerSeries(true);
          }}
        />
      )}
      {accion && accion.tipo === "cancel" && tieneSerie(accion.turno) && (
        <CancelarSerieModal
          turno={accion.turno}
          fecha={date}
          busy={ocupado}
          onSolo={ejecutarAccion}
          onSiguientes={cancelarSiguientes}
          onVolver={() => setAccion(null)}
        />
      )}
      {mantenidas && <FechasMantenidasModal resultado={mantenidas} onClose={() => setMantenidas(null)} />}
      {accion && !(accion.tipo === "cancel" && tieneSerie(accion.turno)) && (
        <ConfirmModal
          title={ACCIONES[accion.tipo].titulo}
          confirmLabel={ACCIONES[accion.tipo].boton}
          busy={ocupado}
          onConfirm={ejecutarAccion}
          onCancel={() => setAccion(null)}
        >
          {textoAccion(accion.tipo, accion.turno)}
        </ConfirmModal>
      )}
      {aBorrar && (
        <ConfirmModal title="Eliminar bloqueo" confirmLabel="Eliminar" busy={ocupado} onConfirm={borrarBloqueo} onCancel={() => setABorrar(null)}>
          {aBorrar.scope === "salon"
            ? "Este bloqueo es de todo el salón: se libera para todo el equipo."
            : "Se libera ese horario en la agenda."}
        </ConfirmModal>
      )}
      {mover && (
        <RescheduleModal
          title="Mover turno"
          summary={`${nombreCliente(mover)} · ${mover.professionalName || "Sin profesional"} · ${mover.slotStart || ""} hs`}
          initialDate={mover.date || date}
          professionals={columnas.filter((c) => c.active)}
          loadOptions={(d, professionalId) => beautyRescheduleOptionsApi(mover._id, { date: d, professionalId })}
          onConfirm={(body) => beautyRescheduleAppointmentApi(mover._id, body)}
          onClose={() => setMover(null)}
          onDone={movido}
        />
      )}
      {reserva && (
        <NuevaReservaModal
          profesionales={columnas}
          inicial={reserva}
          esEmpleado={esEmpleado}
          onClose={() => setReserva(null)}
          onCreated={() => creado("Turno creado")}
          onSeriesCreated={(resultado) => creado(seriesCreatedMessage(resultado))}
        />
      )}
      {verSeries && <TurnosFijosModal salon={salon} onClose={() => setVerSeries(false)} onCancelled={refrescar} />}
      {bloqueo && (
        <BloqueoModal
          salon={salon}
          profesionales={columnas}
          inicial={bloqueo}
          esEmpleado={esEmpleado}
          onClose={() => setBloqueo(null)}
          onCreated={() => creado("Horario bloqueado")}
        />
      )}
      <OrgToast message={toast} onClose={cerrarToast} />
    </div>
  </div>
);
};

export default AgendaTab;
